import { fetchApi } from './api';

const BASE = 'supply_management/load-estimates';

export type LoadEstimateStatus = 'draft' | 'ordered' | 'discarded';

export interface LoadEstimateLinePayload {
  product_id: number;
  supplier_id: number;
  quantity: number;
  suggested_quantity?: number;
  is_user_edited?: boolean;
}

export interface LoadEstimateLine extends Omit<LoadEstimateLinePayload, 'quantity' | 'suggested_quantity'> {
  id: number;
  quantity: string;
  suggested_quantity: string;
}

export interface LoadEstimateDraftPayload {
  warehouse_id?: number | null;
  production_week?: string | null;
  so_external_id?: string;
  apply_moq_mp?: boolean;
  /** {supplier_id: código de contenedor} */
  container_by_supplier?: Record<string, string>;
  lines?: LoadEstimateLinePayload[];
}

export interface LoadEstimateDraft {
  id: number;
  status: LoadEstimateStatus;
  warehouse_id: number | null;
  production_week: string | null;
  so_external_id: string;
  apply_moq_mp: boolean;
  container_by_supplier: Record<string, string>;
  lines: LoadEstimateLine[];
  created_at: string;
  updated_at: string;
  ordered_at: string | null;
}

export interface LoadEstimatePendingSupplier {
  supplier_id: number;
  supplier_name: string;
  products: number;
  units: number;
  value: number;
  /** Productos validados del proveedor con la cantidad a ordenar. */
  lines: Array<{ product_id: number; sku: string; product_name: string; quantity: number }>;
}

/** Proveedor sin productos validados pero con pedido sugerido: mismas líneas que uno validado. */
export type LoadEstimateAwaitingSupplier = LoadEstimatePendingSupplier;

/** Stock, tránsito, ventas y cobertura de un producto, con las cifras de la tabla de compras. */
export interface LoadEstimateProductContext {
  stock: number | null;
  in_transit: number | null;
  monthly_sales: number | null;
  estimated_monthly_sales: number | null;
  coverage_days: number | null;
}

export interface LoadEstimateCreateOrdersResponse {
  success: boolean;
  total_created: number;
  total_updated: number;
  suppliers_processed: number;
  /** Productos de la estimación sin sugerencia activa: no entraron en la orden. */
  skipped_products?: number;
}

type Paginated<T> = T[] | { results: T[] };

const rows = <T,>(data: Paginated<T>): T[] => (Array.isArray(data) ? data : data.results ?? []);

export const loadEstimateService = {
  /** Proveedores con productos validados por ordenar: punto de partida del módulo. */
  pendingSuppliers(warehouseId: number, applyMoqMp = true): Promise<LoadEstimatePendingSupplier[]> {
    return fetchApi(`${BASE}/pending-suppliers/`, { params: { warehouse: warehouseId, apply_moq_mp: String(applyMoqMp) } });
  },

  /** Proveedores con sugeridos sin validar: lo que falta validar en la bandeja para poder estimarlos. */
  awaitingValidation(warehouseId: number, applyMoqMp = true): Promise<LoadEstimateAwaitingSupplier[]> {
    return fetchApi(`${BASE}/awaiting-validation/`, { params: { warehouse: warehouseId, apply_moq_mp: String(applyMoqMp) } });
  },

  /** Datos de contexto de unos productos; se piden por tandas para no pasar el límite de la URL. */
  async productContext(warehouseId: number, productIds: number[]): Promise<Record<string, LoadEstimateProductContext>> {
    const chunks: number[][] = [];
    for (let index = 0; index < productIds.length; index += 300) chunks.push(productIds.slice(index, index + 300));
    const parts = await Promise.all(chunks.map((ids) => fetchApi<Record<string, LoadEstimateProductContext>>(
      `${BASE}/product-context/`,
      { params: { warehouse: warehouseId, product_ids: ids.join(',') } },
    )));
    return Object.assign({}, ...parts);
  },

  /** Clasificación ABC por producto ({product_id: 'A' | 'B' | 'C'}). */
  productAbc(warehouseId: number): Promise<Record<string, string>> {
    return fetchApi(`${BASE}/product-abc/`, { params: { warehouse: warehouseId } });
  },

  async listDrafts(warehouseId: number): Promise<LoadEstimateDraft[]> {
    return rows(await fetchApi<Paginated<LoadEstimateDraft>>(`${BASE}/`, { params: { warehouse: warehouseId } }));
  },

  create(payload: LoadEstimateDraftPayload): Promise<LoadEstimateDraft> {
    return fetchApi(`${BASE}/`, { method: 'POST', body: payload });
  },

  update(id: number, payload: LoadEstimateDraftPayload): Promise<LoadEstimateDraft> {
    return fetchApi(`${BASE}/${id}/`, { method: 'PATCH', body: payload });
  },

  discard(id: number): Promise<void> {
    return fetchApi(`${BASE}/${id}/`, { method: 'DELETE' });
  },

  createOrders(id: number): Promise<LoadEstimateCreateOrdersResponse> {
    return fetchApi(`${BASE}/${id}/create-orders/`, { method: 'POST' });
  },
};
